<template>
  <div class="oauth-client-page">
    <div class="oauth-client-panel">
    <header class="page-heading">
      <div>
        <div class="eyebrow">OPEN API AUTHORIZATION</div>
        <h1>开放接口授权</h1>
        <p>管理开放接口调用方及其访问凭证。客户端密钥仅在创建或重置后显示一次。</p>
      </div>
    </header>

    <div class="filter-panel">
      <el-input
        v-model.trim="query.clientName"
        clearable
        placeholder="客户端名称"
        @keyup.enter.native="search"
      />
      <el-input
        v-model="query.tenantId"
        clearable
        placeholder="租户 ID"
        @keyup.enter.native="search"
      />
      <el-select v-model="query.status" clearable placeholder="全部状态" popper-class="oauth-select-dropdown">
        <el-option label="启用" :value="1" />
        <el-option label="禁用" :value="0" />
      </el-select>
      <div class="filter-actions">
        <el-button type="primary" icon="el-icon-search" @click="search">查询</el-button>
        <el-button type="primary" icon="el-icon-plus" @click="openCreate">新增客户端</el-button>
      </div>
    </div>

    <div class="table-panel">
      <div class="table-wrap">
      <el-table
        class="oauth-client-table"
        v-loading="loading"
        :data="tableData"
        border
        height="100%"
        style="width: 100%;"
        empty-text="暂无 API 客户端"
      >
        <el-table-column prop="id" label="Client ID" min-width="100" align="center" />
        <el-table-column prop="clientName" label="客户端名称" min-width="180" align="center" show-overflow-tooltip />
        <el-table-column prop="tenantId" label="租户 ID" min-width="110" align="center" />
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'info'" size="small">
              {{ scope.row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="创建时间" min-width="170" align="center">
          <template slot-scope="scope">{{ formatTime(scope.row.createTime) }}</template>
        </el-table-column>
        <el-table-column label="更新时间" min-width="170" align="center">
          <template slot-scope="scope">{{ formatTime(scope.row.updateTime) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="330" fixed="right" align="center">
          <template slot-scope="scope">
            <div class="action-group">
              <el-button type="text" @click="openUsage(scope.row)">调用统计</el-button>
              <el-button type="text" @click="openEdit(scope.row)">编辑</el-button>
              <el-button type="text" @click="resetSecret(scope.row)">重置密钥</el-button>
              <el-button type="text" class="danger-action" @click="remove(scope.row)">删除</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
      </div>

      <div class="pagination-box">
      <el-pagination
        class="pagination"
        background
        layout="prev, pager, next, jumper"
        :current-page="query.pageNum"
        :page-size="query.pageSize"
        :total="total"
        @current-change="changePage"
      />
      </div>
    </div>
    </div>

    <usage-dialog :visible.sync="usageVisible" :client="usageClient" />

    <el-dialog
      :title="form.id ? '编辑 API 客户端' : '新增 API 客户端'"
      :visible.sync="formVisible"
      width="520px"
      custom-class="oauth-client-dialog"
      append-to-body
      @closed="resetForm"
    >
      <el-form ref="clientForm" :model="form" :rules="rules" label-width="110px">
        <el-form-item label="客户端名称" prop="clientName">
          <el-input v-model.trim="form.clientName" maxlength="100" show-word-limit />
        </el-form-item>
        <el-form-item label="租户" prop="tenantId">
          <el-select
            v-model="form.tenantId"
            class="tenant-select"
            filterable
            remote
            clearable
            :remote-method="searchTenants"
            :loading="tenantLoading"
            placeholder="输入租户 ID、名称或账号搜索"
            popper-class="oauth-select-dropdown"
            @visible-change="handleTenantSelectVisible"
          >
            <el-option
              v-for="tenant in tenantOptions"
              :key="tenant.id"
              :label="tenantLabel(tenant)"
              :value="tenant.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button class="secondary-button" @click="formVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submit">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="请立即保存客户端密钥"
      :visible.sync="credentialVisible"
      width="600px"
      custom-class="oauth-client-dialog credential-dialog"
      append-to-body
      :close-on-click-modal="false"
    >
      <el-alert
        title="关闭后将无法再次查看该密钥。如密钥丢失，只能重新生成。"
        type="warning"
        :closable="false"
        show-icon
      />
      <div class="credential-row">
        <span>Client ID</span>
        <code>{{ credential.clientId }}</code>
      </div>
      <div class="credential-row">
        <span>Client Secret</span>
        <code>{{ credential.clientSecret }}</code>
      </div>
      <span slot="footer">
        <el-button
          v-clipboard:copy="credentialText"
          v-clipboard:success="copySuccess"
          v-clipboard:error="copyError"
          type="primary"
          icon="el-icon-document-copy"
        >复制凭证</el-button>
        <el-button class="secondary-button" @click="credentialVisible = false">我已保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import UsageDialog from './UsageDialog.vue';

const emptyForm = () => ({
  id: 0,
  clientName: '',
  tenantId: '',
  status: 1
});

export default {
  components: { UsageDialog },
  data() {
    return {
      loading: false,
      saving: false,
      tenantLoading: false,
      tenantRequestSerial: 0,
      formVisible: false,
      credentialVisible: false,
      usageVisible: false,
      usageClient: null,
      tableData: [],
      tenantOptions: [],
      total: 0,
      query: {
        pageNum: 1,
        pageSize: 10,
        clientName: '',
        tenantId: '',
        status: ''
      },
      form: emptyForm(),
      credential: {
        clientId: '',
        clientSecret: ''
      },
      rules: {
        clientName: [
          { required: true, message: '请输入客户端名称', trigger: 'blur' },
          { max: 100, message: '名称不能超过 100 个字符', trigger: 'blur' }
        ],
        tenantId: [{ required: true, message: '请选择租户', trigger: 'change' }],
        status: [{ required: true, message: '请选择状态', trigger: 'change' }]
      }
    };
  },
  computed: {
    credentialText() {
      return `client_id=${this.credential.clientId}\nclient_secret=${this.credential.clientSecret}`;
    }
  },
  mounted() {
    if (Number(localStorage.getItem('a-xny-level')) !== 1) {
      this.$message.warning('仅平台管理员可以访问开放接口授权管理');
      this.$router.replace({ name: 'indexVue' });
      return;
    }
    document.title = '开放接口授权';
    this.loadPage();
  },
  methods: {
    openUsage(row) {
      this.usageClient = { id: row.id, clientName: row.clientName };
      this.usageVisible = true;
    },
    normalizePayload(source) {
      return {
        id: source.id || 0,
        clientName: source.clientName,
        tenantId: Number(source.tenantId),
        status: source.status
      };
    },
    loadPage() {
      this.loading = true;
      const payload = {
        ...this.query,
        tenantId: this.query.tenantId ? Number(this.query.tenantId) : null,
        status: this.query.status === '' ? null : this.query.status
      };
      this.$request.post('/api/oauth/client/page', payload).then(response => {
        if (response.data.result) {
          const data = response.data.data || {};
          this.tableData = data.list || [];
          this.total = data.totalCount || 0;
        } else {
          this.$message.error(response.data.message || '获取 API 客户端列表失败');
        }
      }).catch(() => {
        this.$message.error('网络异常，获取 API 客户端列表失败');
      }).finally(() => {
        this.loading = false;
      });
    },
    search() {
      this.query.pageNum = 1;
      this.loadPage();
    },
    changePage(page) {
      this.query.pageNum = page;
      this.loadPage();
    },
    openCreate() {
      this.form = emptyForm();
      this.formVisible = true;
      this.searchTenants('');
    },
    openEdit(row) {
      this.form = {
        id: row.id,
        clientName: row.clientName,
        tenantId: row.tenantId,
        status: row.status
      };
      this.formVisible = true;
      this.searchTenants('');
    },
    resetForm() {
      this.tenantRequestSerial += 1;
      this.tenantLoading = false;
      this.form = emptyForm();
      this.tenantOptions = [];
      if (this.$refs.clientForm) {
        this.$refs.clientForm.clearValidate();
      }
    },
    searchTenants(keyword) {
      const requestSerial = ++this.tenantRequestSerial;
      this.tenantLoading = true;
      this.$request.get('/api/oauth/client/tenant-options', {
        params: { keyword: (keyword || '').trim() }
      }).then(response => {
        if (requestSerial !== this.tenantRequestSerial) return;
        if (response.data.result) {
          this.tenantOptions = response.data.data || [];
        } else {
          this.$message.error(response.data.message || '获取租户列表失败');
        }
      }).catch(() => {
        if (requestSerial === this.tenantRequestSerial) {
          this.$message.error('网络异常，获取租户列表失败');
        }
      }).finally(() => {
        if (requestSerial === this.tenantRequestSerial) {
          this.tenantLoading = false;
        }
      });
    },
    handleTenantSelectVisible(visible) {
      if (visible) this.searchTenants('');
    },
    tenantLabel(tenant) {
      const name = tenant.userName || '未命名租户';
      return tenant.account
        ? `${tenant.id} · ${name}（${tenant.account}）`
        : `${tenant.id} · ${name}`;
    },
    submit() {
      this.$refs.clientForm.validate(valid => {
        if (!valid) return;
        this.saving = true;
        const isEdit = this.form.id > 0;
        const url = isEdit ? '/api/oauth/client/edit' : '/api/oauth/client/add';
        this.$request.post(url, this.normalizePayload(this.form)).then(response => {
          if (!response.data.result) {
            this.$message.error(response.data.message || '保存失败');
            return;
          }
          this.formVisible = false;
          this.$message.success(isEdit ? '修改成功' : '创建成功');
          if (!isEdit) {
            this.showCredential(response.data.data);
          }
          this.loadPage();
        }).catch(error => {
          this.$message.error(this.errorMessage(error, '保存失败'));
        }).finally(() => {
          this.saving = false;
        });
      });
    },
    resetSecret(row) {
      this.$confirm(`重置后 Client「${row.clientName}」的旧密钥将立即失效，是否继续？`, '重置密钥', {
        type: 'warning',
        customClass: 'oauth-confirm-dialog'
      }).then(() => {
        return this.$request.post(`/api/oauth/client/reset-secret/${row.id}`);
      }).then(response => {
        if (response.data.result) {
          this.showCredential(response.data.data);
          this.$message.success('密钥已重置');
        } else {
          this.$message.error(response.data.message || '重置密钥失败');
        }
      }).catch(error => {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error(this.errorMessage(error, '重置密钥失败'));
        }
      });
    },
    remove(row) {
      this.$confirm(`删除后 Client「${row.clientName}」将无法继续访问开放接口，是否继续？`, '删除 Client', {
        type: 'warning',
        customClass: 'oauth-confirm-dialog'
      }).then(() => {
        return this.$request.delete(`/api/oauth/client/delete/${row.id}`);
      }).then(response => {
        if (response.data.result) {
          this.$message.success('删除成功');
          if (this.tableData.length === 1 && this.query.pageNum > 1) {
            this.query.pageNum -= 1;
          }
          this.loadPage();
        } else {
          this.$message.error(response.data.message || '删除失败');
        }
      }).catch(error => {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error(this.errorMessage(error, '删除失败'));
        }
      });
    },
    showCredential(data) {
      this.credential = data || { clientId: '', clientSecret: '' };
      this.credentialVisible = true;
    },
    copySuccess() {
      this.$message.success('凭证已复制');
    },
    copyError() {
      this.$message.error('复制失败，请手动复制');
    },
    errorMessage(error, fallback) {
      return error && error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : fallback;
    },
    formatTime(value) {
      if (!value) return '-';
      return String(value).replace('T', ' ').replace(/\.\d+.*$/, '');
    }
  }
};
</script>

<style scoped>
.oauth-client-page {
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  color: var(--solar-text-strong, #dff5ff);
  background:
    linear-gradient(135deg, rgba(var(--solar-primary-rgb, 0, 153, 255), 0.08), transparent 34%),
    var(--solar-page, #001a33);
  box-sizing: border-box;
  overflow: hidden;
}

.oauth-client-panel {
  width: 100%;
  max-width: 100%;
  height: calc(100vh - 104px);
  max-height: calc(100vh - 104px);
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(var(--solar-border-strong-rgb, 63, 184, 255), 0.45);
  border-radius: 8px;
  background: rgba(var(--solar-page-rgb, 0, 31, 61), 0.92);
  box-shadow: 0 18px 40px rgba(0, 7, 18, 0.32), inset 0 1px 0 rgba(176, 226, 255, 0.18);
  box-sizing: border-box;
}

.page-heading {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 0;
  padding: 16px 22px 14px;
  border-bottom: 1px solid rgba(var(--solar-border-strong-rgb, 63, 184, 255), 0.28);
  background: linear-gradient(90deg, rgba(var(--solar-surface-rgb, 0, 43, 77), 0.92), rgba(var(--solar-page-rgb, 0, 26, 51), 0.72));
}

.page-heading h1 {
  margin: 5px 0 4px;
  color: var(--solar-text-strong, #f1fbff);
  font-size: 25px;
}

.page-heading p {
  margin: 0;
  color: var(--solar-text, #87bad0);
  font-size: 13px;
}

.eyebrow {
  color: var(--solar-primary, #29d8ff);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 2px;
}

.filter-panel,
.table-panel {
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

.filter-panel {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 16px;
  min-height: 64px;
  padding: 14px 22px;
  margin: 0;
  border-bottom: 1px solid rgba(var(--solar-border-strong-rgb, 63, 184, 255), 0.2);
  background: rgba(var(--solar-page-rgb, 0, 26, 51), 0.34);
  box-sizing: border-box;
}

.filter-panel .el-input,
.filter-panel .el-select {
  width: 240px;
}

.filter-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}

.filter-actions .el-button {
  margin: 0;
}

.table-panel {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 16px 22px 0;
  overflow: hidden;
  background: transparent;
  box-sizing: border-box;
}

.table-wrap {
  flex: 1 1 0;
  height: 0;
  min-height: 0;
  overflow: hidden;
  border: 1px solid rgba(var(--solar-border-strong-rgb, 63, 184, 255), 0.28);
  border-radius: 8px;
  background: rgba(var(--solar-page-rgb, 0, 18, 36), 0.42);
  box-shadow: 0 10px 22px rgba(0, 10, 24, 0.18);
}

.pagination-box {
  flex: 0 0 auto;
  min-height: 58px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 10px 0;
  background: rgba(var(--solar-page-rgb, 0, 18, 36), 0.16);
  box-sizing: border-box;
}

.pagination {
  margin-top: 0;
  text-align: right;
}

.danger-action {
  color: var(--solar-danger, #ff6b75);
}

.action-group {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
}

.oauth-client-page /deep/ .action-group .el-button--text {
  min-width: 0;
  margin: 0 !important;
  padding: 5px 8px !important;
  border: 1px solid rgba(var(--solar-border-strong-rgb, 55, 174, 230), 0.42) !important;
  border-radius: 4px;
  background: rgba(var(--solar-surface-rgb, 2, 46, 78), 0.72);
  color: var(--solar-text, #83d9ff);
  line-height: 18px;
}

.oauth-client-page /deep/ .action-group .el-button--text:hover,
.oauth-client-page /deep/ .action-group .el-button--text:focus {
  border-color: var(--solar-border-strong, #2bc8ff) !important;
  background: rgba(var(--solar-raised-rgb, 0, 83, 126), 0.82);
  color: var(--solar-text-strong, #ffffff);
}

.oauth-client-page /deep/ .action-group .danger-action {
  border-color: rgba(255, 107, 117, 0.46) !important;
  background: rgba(112, 31, 43, 0.18);
  color: var(--solar-danger, #ff8790);
}

.oauth-client-page /deep/ .action-group .danger-action:hover,
.oauth-client-page /deep/ .action-group .danger-action:focus {
  border-color: #ff6b75 !important;
  background: rgba(140, 37, 49, 0.34);
  color: #ffd5d8;
}

.credential-row {
  display: grid;
  grid-template-columns: 110px minmax(0, 1fr);
  gap: 14px;
  align-items: start;
  margin-top: 18px;
}

.tenant-select {
  width: 100%;
}

.credential-row span {
  color: var(--solar-text, #9dccdf);
  line-height: 38px;
}

.credential-row code {
  display: block;
  padding: 10px 12px;
  border: 1px solid rgba(var(--solar-border-strong-rgb, 40, 199, 255), 0.3);
  border-radius: 4px;
  background: var(--solar-page, #001a33);
  color: var(--solar-text-strong, #b9edff);
  overflow-wrap: anywhere;
  user-select: all;
}

.oauth-client-page /deep/ .el-input__inner {
  border-color: rgba(var(--solar-border-strong-rgb, 57, 166, 220), 0.34);
  background: rgba(var(--solar-page-rgb, 0, 22, 43), 0.86);
  color: var(--solar-text-strong, #dff5ff);
}

.oauth-client-page /deep/ .el-input {
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
}

.oauth-client-page /deep/ .el-input__count,
.oauth-client-page /deep/ .el-input__count-inner {
  border: 0 !important;
  background: transparent !important;
  color: var(--solar-muted, #76b7d0) !important;
}

.oauth-client-page /deep/ .el-input__inner:hover,
.oauth-client-page /deep/ .el-input__inner:focus {
  border-color: var(--solar-border-strong, #28c7ff);
}

.oauth-client-page /deep/ .el-input__inner::placeholder,
.oauth-client-page /deep/ .el-input__icon,
.oauth-client-page /deep/ .el-select__caret {
  color: var(--solar-muted, #6fabc5);
}

.oauth-client-page /deep/ .secondary-button {
  border-color: rgba(var(--solar-border-strong-rgb, 57, 166, 220), 0.38);
  background: rgba(var(--solar-surface-rgb, 0, 42, 72), 0.86);
  color: var(--solar-text-strong, #aee7ff);
}

.oauth-client-page /deep/ .secondary-button:hover,
.oauth-client-page /deep/ .secondary-button:focus {
  border-color: var(--solar-border-strong, #28c7ff);
  background: rgba(var(--solar-raised-rgb, 0, 70, 108), 0.9);
  color: var(--solar-text-strong, #ffffff);
}

.oauth-client-page /deep/ .oauth-client-table.el-table,
.oauth-client-page /deep/ .oauth-client-table .el-table__header-wrapper,
.oauth-client-page /deep/ .oauth-client-table .el-table__body-wrapper,
.oauth-client-page /deep/ .oauth-client-table .el-table__fixed,
.oauth-client-page /deep/ .oauth-client-table .el-table__fixed-right {
  background: transparent;
  color: var(--solar-text-strong, #dff5ff);
}

.oauth-client-page /deep/ .oauth-client-table.el-table--border {
  border: 0 !important;
  border-radius: 0;
}

.oauth-client-page /deep/ .oauth-client-table.el-table::before,
.oauth-client-page /deep/ .oauth-client-table.el-table::after,
.oauth-client-page /deep/ .oauth-client-table .el-table__fixed::before,
.oauth-client-page /deep/ .oauth-client-table .el-table__fixed-right::before {
  display: none !important;
}

.oauth-client-page /deep/ .oauth-client-table th,
.oauth-client-page /deep/ .oauth-client-table .el-table__fixed-right-patch {
  border-color: rgba(var(--solar-border-strong-rgb, 57, 166, 220), 0.24) !important;
  background: rgba(var(--solar-page-rgb, 0, 24, 48), 0.96) !important;
  color: var(--solar-text-strong, #bcecff);
}

.oauth-client-page /deep/ .oauth-client-table td {
  border-color: rgba(var(--solar-border-strong-rgb, 57, 166, 220), 0.18) !important;
  background: rgba(var(--solar-surface-rgb, 0, 36, 66), 0.88) !important;
  color: var(--solar-text-strong, #d7f4ff);
}

.oauth-client-page /deep/ .oauth-client-table .el-table__body tr:hover > td,
.oauth-client-page /deep/ .oauth-client-table .el-table__body tr.hover-row > td {
  background: rgba(var(--solar-raised-rgb, 0, 65, 100), 0.98) !important;
}

.oauth-client-page /deep/ .oauth-client-table .el-table__empty-block {
  background: rgba(var(--solar-page-rgb, 0, 31, 58), 0.9);
}

.oauth-client-page /deep/ .oauth-client-table .el-table__empty-text {
  color: var(--solar-muted, #79b8d3);
}

.oauth-client-page /deep/ .oauth-client-table .el-tag--success {
  border-color: rgba(62, 213, 142, 0.42);
  background: rgba(25, 132, 88, 0.2);
  color: var(--solar-text, #73e9b1);
}

.oauth-client-page /deep/ .oauth-client-table .el-tag--info {
  border-color: rgba(132, 166, 184, 0.36);
  background: rgba(72, 99, 116, 0.22);
  color: var(--solar-text, #a9c6d5);
}

.oauth-client-page /deep/ .oauth-client-table .el-loading-mask {
  background: rgba(var(--solar-page-rgb, 0, 22, 43), 0.8);
}

.oauth-client-page /deep/ .el-pagination,
.oauth-client-page /deep/ .el-pagination__total,
.oauth-client-page /deep/ .el-pagination__jump {
  color: var(--solar-text, #8dc8df);
}

.oauth-client-page /deep/ .pagination.el-pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 32px;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  line-height: 1;
  box-sizing: border-box;
}

.oauth-client-page /deep/ .pagination.el-pagination > span,
.oauth-client-page /deep/ .pagination.el-pagination > button,
.oauth-client-page /deep/ .pagination.el-pagination .el-pager,
.oauth-client-page /deep/ .pagination.el-pagination .el-pager li,
.oauth-client-page /deep/ .pagination.el-pagination .el-pagination__jump,
.oauth-client-page /deep/ .pagination.el-pagination .el-pagination__editor {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  line-height: 32px;
  box-sizing: border-box;
}

.oauth-client-page /deep/ .pagination.el-pagination .el-pagination__total {
  margin-right: 12px;
}

.oauth-client-page /deep/ .pagination.el-pagination .el-pagination__jump {
  margin-left: 14px;
}

.oauth-client-page /deep/ .pagination.el-pagination .el-pagination__editor {
  width: 44px !important;
  height: 32px !important;
  margin: 0 6px;
  padding: 0 2px !important;
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  overflow: visible !important;
}

.oauth-client-page /deep/ .pagination.el-pagination .el-pagination__editor .el-input__inner {
  width: 40px !important;
  height: 32px !important;
  padding: 0 6px !important;
  border: 1px solid var(--solar-border-strong, #0099ff) !important;
  border-radius: 4px !important;
  background: var(--solar-page, #001a33) !important;
  box-shadow: none !important;
  line-height: 32px !important;
  text-align: center;
  box-sizing: border-box;
}

.oauth-client-page /deep/ .el-pagination.is-background .btn-prev,
.oauth-client-page /deep/ .el-pagination.is-background .btn-next,
.oauth-client-page /deep/ .el-pagination.is-background .el-pager li {
  border: 1px solid rgba(var(--solar-border-strong-rgb, 57, 166, 220), 0.25);
  background: rgba(var(--solar-page-rgb, 0, 28, 52), 0.9);
  color: var(--solar-text, #9ed9ef);
}

.oauth-client-page /deep/ .el-pagination.is-background .el-pager li.active {
  border-color: var(--solar-border-strong, #159ed8);
  background: var(--solar-primary, #087eaf);
  color: var(--solar-text-strong, #ffffff);
}

@media (max-width: 960px) {
  .filter-panel {
    flex-wrap: wrap;
  }

  .filter-panel .el-input,
  .filter-panel .el-select {
    width: calc(50% - 6px);
  }
}
</style>

<style>
.oauth-select-dropdown.el-select-dropdown {
  border-color: rgba(var(--solar-border-strong-rgb, 57, 166, 220), 0.34);
  background: var(--solar-surface, #032b4b);
  box-shadow: 0 12px 30px rgba(0, 7, 18, 0.42);
}

.oauth-select-dropdown .el-select-dropdown__item {
  color: var(--solar-text-strong, #bcecff);
}

.oauth-select-dropdown .el-select-dropdown__item.hover,
.oauth-select-dropdown .el-select-dropdown__item:hover {
  background: var(--solar-raised, #075077);
}

.oauth-select-dropdown .el-select-dropdown__item.selected {
  color: var(--solar-text, #42d4ff);
}

.oauth-select-dropdown .popper__arrow,
.oauth-select-dropdown .popper__arrow::after {
  border-bottom-color: var(--solar-border, #032b4b) !important;
}

.oauth-confirm-dialog.el-message-box {
  border-color: rgba(var(--solar-border-strong-rgb, 57, 166, 220), 0.36);
  background: var(--solar-surface, #032b4b);
  box-shadow: 0 20px 60px rgba(0, 7, 18, 0.56);
}

.oauth-confirm-dialog .el-message-box__title,
.oauth-confirm-dialog .el-message-box__message,
.oauth-confirm-dialog .el-message-box__close {
  color: var(--solar-text-strong, #c9efff);
}

.oauth-confirm-dialog .el-button--default {
  border-color: rgba(var(--solar-border-strong-rgb, 57, 166, 220), 0.38);
  background: var(--solar-surface, #052f50);
  color: var(--solar-text-strong, #aee7ff);
}

.oauth-client-dialog.el-dialog {
  border: 1px solid rgba(var(--solar-border-strong-rgb, 57, 166, 220), 0.35);
  background: var(--solar-surface, #032b4b);
  box-shadow: 0 20px 60px rgba(0, 7, 18, 0.56);
}

.oauth-client-dialog .el-dialog__header {
  padding: 18px 20px;
  border-bottom: 1px solid rgba(var(--solar-border-strong-rgb, 57, 166, 220), 0.24);
  background: var(--solar-page, #001a33);
}

.oauth-client-dialog .el-dialog__title,
.oauth-client-dialog .el-dialog__close {
  color: var(--solar-text-strong, #bcecff);
}

.oauth-client-dialog .el-dialog__body,
.oauth-client-dialog .el-dialog__footer {
  background: var(--solar-surface, #032b4b);
  color: var(--solar-text-strong, #dff5ff);
}

.oauth-client-dialog .el-dialog__footer {
  padding-top: 12px;
  border-top: 1px solid rgba(var(--solar-border-strong-rgb, 57, 166, 220), 0.16);
}

.oauth-client-dialog .el-form-item__label,
.oauth-client-dialog .el-radio {
  color: var(--solar-text-strong, #a9dbea);
}

.oauth-client-dialog .el-radio__inner {
  border-color: #3c7e9c;
  background: var(--solar-page, #001a33);
}

.oauth-client-dialog .el-radio__input.is-checked .el-radio__inner {
  border-color: var(--solar-border-strong, #28c7ff);
  background: var(--solar-primary, #149fd5);
}

.oauth-client-dialog .el-input {
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  vertical-align: top;
}

.oauth-client-dialog .tenant-select.el-select {
  height: auto;
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  overflow: visible;
  vertical-align: top;
}

.oauth-client-dialog .el-input__inner {
  border-color: rgba(var(--solar-border-strong-rgb, 57, 166, 220), 0.34);
  background: rgba(var(--solar-page-rgb, 0, 22, 43), 0.86);
  color: var(--solar-text-strong, #dff5ff);
}

.oauth-client-dialog .el-input__inner:hover,
.oauth-client-dialog .el-input__inner:focus {
  border-color: var(--solar-border-strong, #28c7ff);
}

.oauth-client-dialog .el-input__inner::placeholder,
.oauth-client-dialog .el-input__icon {
  color: var(--solar-muted, #6fabc5);
}

.oauth-client-dialog .el-input__count,
.oauth-client-dialog .el-input__count-inner {
  border: 0 !important;
  background: transparent !important;
  color: var(--solar-muted, #76b7d0) !important;
}

.credential-dialog .el-alert--warning {
  border: 1px solid rgba(230, 170, 54, 0.34);
  background: rgba(119, 76, 8, 0.28);
  color: var(--solar-warning, #f3d28b);
}

.credential-dialog .el-alert__title,
.credential-dialog .el-alert__icon {
  color: var(--solar-warning, #f3d28b);
}
</style>
